import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Zap, Clock, Shield, RefreshCw, CheckCircle, AlertCircle, Cpu, TrendingUp, Server } from 'lucide-react';
import OrgChart, { flattenOrg, FALLBACK_ORG } from '../components/OrgChart';
import TaskPanel from '../components/TaskPanel';

const ORCHESTRATOR_URL = 'http://localhost:8000';
const METRICS_INTERVAL = 15000;

function MetricCard({ icon: Icon, label, value, unit, trend, color = 'blue' }) {
  const colorMap = {
    blue: 'text-blue-400 bg-blue-900/20 border-blue-500/20',
    green: 'text-green-400 bg-green-900/20 border-green-500/20',
    purple: 'text-purple-400 bg-purple-900/20 border-purple-500/20',
    yellow: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/20',
    red: 'text-red-400 bg-red-900/20 border-red-500/20',
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${colorMap[color]}`}>
      <div className="flex-shrink-0">
        <Icon className="w-5 h-5 opacity-80" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 truncate">{label}</p>
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-bold text-white font-mono">
            {value != null ? value : '--'}
          </span>
          {unit && <span className="text-xs text-slate-500">{unit}</span>}
        </div>
      </div>
      {trend != null && (
        <div className="ml-auto flex-shrink-0">
          <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-green-400' : 'text-red-400 rotate-180'}`} />
        </div>
      )}
    </div>
  );
}

function ActivityFeedItem({ entry }) {
  const ts = entry.timestamp
    ? new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--';

  return (
    <div className="flex items-start gap-2.5 py-2.5 border-b border-slate-700/30 last:border-0">
      <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${
        entry.type === 'error' ? 'bg-red-500' :
        entry.type === 'success' ? 'bg-green-500' :
        entry.type === 'warning' ? 'bg-yellow-500' :
        'bg-[#7c3aed]'
      }`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-300 line-clamp-2">{entry.message}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {entry.agent && (
            <span className="text-[10px] text-slate-500">{entry.agent}</span>
          )}
          <span className="text-[10px] text-slate-600 ml-auto">{ts}</span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [agents, setAgents] = useState(flattenOrg(FALLBACK_ORG));
  const [metrics, setMetrics] = useState({
    totalTasks: null,
    activeAgents: null,
    avgResponseMs: null,
    uptime: null,
    errorRate: null,
  });
  const [activityFeed, setActivityFeed] = useState([
    { id: 1, type: 'info', message: 'CAO Platform POC initialized — waiting for orchestrator.', timestamp: new Date().toISOString() },
  ]);
  const [metricsError, setMetricsError] = useState(false);

  // Fetch metrics from orchestrator
  const fetchMetrics = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);

      const [metricsRes, orgRes] = await Promise.allSettled([
        fetch(`${ORCHESTRATOR_URL}/metrics`, { signal: controller.signal }),
        fetch(`${ORCHESTRATOR_URL}/org`, { signal: controller.signal }),
      ]);
      clearTimeout(timeout);

      // Process metrics
      if (metricsRes.status === 'fulfilled' && metricsRes.value.ok) {
        const data = await metricsRes.value.json();
        setMetrics({
          totalTasks: data.total_tasks ?? data.tasks_total ?? null,
          activeAgents: data.active_agents ?? data.agents_online ?? null,
          avgResponseMs: data.avg_response_ms ?? data.latency_p50_ms ?? null,
          uptime: data.uptime_seconds ?? null,
          errorRate: data.error_rate ?? null,
        });
        setMetricsError(false);
      } else {
        setMetricsError(true);
      }

      // Process org for agent list
      if (orgRes.status === 'fulfilled' && orgRes.value.ok) {
        const orgData = await orgRes.value.json();
        if (orgData.agents) {
          setAgents(orgData.agents);
        } else if (orgData.name && orgData.role) {
          setAgents(flattenOrg(orgData));
        }

        // Add activity feed entry
        addActivity({
          type: 'success',
          message: 'Org chart refreshed from orchestrator.',
          agent: 'Nellie Ross (CAO)',
        });
      }
    } catch (err) {
      setMetricsError(true);
    }
  }, []);

  function addActivity(entry) {
    setActivityFeed((prev) => [
      { id: Date.now(), timestamp: new Date().toISOString(), ...entry },
      ...prev.slice(0, 49),
    ]);
  }

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, METRICS_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setShowTaskPanel(true);
    addActivity({
      type: 'info',
      message: `Task panel opened for ${agent.name} (${agent.role}).`,
      agent: agent.name,
    });
  };

  const handleTaskPanelClose = () => {
    setShowTaskPanel(false);
    setSelectedAgent(null);
  };

  // Compute active agents from agents list if metrics not available
  const computedActiveAgents = metrics.activeAgents ??
    agents.filter((a) => a.status === 'healthy' || a.status === 'online').length;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: '#0a0e1a', color: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex-shrink-0 border-b border-[#7c3aed]/20 bg-[#0d1117]/80 backdrop-blur-sm">
        <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          {/* Branding */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-[#7c3aed]/30 blur-lg" />
              <div className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#7c3aed] to-[#6d28d9] shadow-lg shadow-[#7c3aed]/30">
                <Shield className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-none">
                CAO Platform
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Org Chart POC &mdash; NeuralQuantum.ai
              </p>
            </div>
          </div>

          {/* Status pills */}
          <div className="hidden md:flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#7c3aed]/10 text-[#7c3aed] border border-[#7c3aed]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7c3aed] animate-pulse" />
              POC Active
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-800/50 text-slate-400 border border-slate-700/50">
              <Server className="w-3 h-3" />
              http://localhost:8000
            </span>
          </div>

          {/* Refresh button */}
          <button
            onClick={fetchMetrics}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/40 transition-all"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {/* ── Metrics Bar ────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-slate-700/30 bg-[#0d1117]/60">
        <div className="max-w-screen-2xl mx-auto px-6 py-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard
              icon={Zap}
              label="Total Tasks"
              value={metrics.totalTasks ?? '—'}
              color="purple"
            />
            <MetricCard
              icon={Activity}
              label="Active Agents"
              value={computedActiveAgents}
              unit={`/ ${agents.length}`}
              color={computedActiveAgents === agents.length ? 'green' : 'yellow'}
            />
            <MetricCard
              icon={Clock}
              label="Avg Response"
              value={metrics.avgResponseMs != null ? Math.round(metrics.avgResponseMs) : '—'}
              unit="ms"
              color="blue"
            />
            <MetricCard
              icon={CheckCircle}
              label="Uptime"
              value={metrics.uptime != null
                ? (() => {
                    const h = Math.floor(metrics.uptime / 3600);
                    const m = Math.floor((metrics.uptime % 3600) / 60);
                    return h > 0 ? `${h}h ${m}m` : `${m}m`;
                  })()
                : '—'}
              color="green"
            />
            <MetricCard
              icon={metrics.errorRate > 0.05 ? AlertCircle : Cpu}
              label="Error Rate"
              value={metrics.errorRate != null ? `${(metrics.errorRate * 100).toFixed(1)}%` : '—'}
              color={metrics.errorRate > 0.05 ? 'red' : 'blue'}
            />
          </div>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────────── */}
      <div className="flex-1 max-w-screen-2xl mx-auto w-full px-6 py-6 flex flex-col gap-6 min-h-0">
        <div className="flex gap-6 flex-1 min-h-0 flex-col xl:flex-row">

          {/* Left: Org Chart */}
          <div className="flex-1 min-w-0 rounded-xl border border-slate-700/40 bg-[#0d1117]/60 overflow-hidden flex flex-col"
               style={{ minHeight: '600px' }}>
            <OrgChart
              onAgentSelect={handleAgentSelect}
              selectedAgent={selectedAgent}
            />
          </div>

          {/* Right: Task Panel + Activity Feed */}
          <div className="w-full xl:w-96 flex flex-col gap-4 flex-shrink-0">
            {/* Task Panel */}
            <div className="flex-1 rounded-xl overflow-hidden" style={{ minHeight: '400px', maxHeight: '600px' }}>
              <TaskPanel
                agents={agents}
                selectedAgent={selectedAgent}
                onClose={showTaskPanel ? handleTaskPanelClose : undefined}
              />
            </div>

            {/* Activity Feed */}
            <div className="rounded-xl border border-slate-700/40 bg-[#0d1117]/60 overflow-hidden flex flex-col"
                 style={{ maxHeight: '280px' }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700/40 bg-[#0a0e1a] flex-shrink-0">
                <Activity className="w-4 h-4 text-[#7c3aed]" />
                <h3 className="text-sm font-semibold text-white">Activity Feed</h3>
                <span className="ml-auto text-xs text-slate-600">{activityFeed.length} events</span>
              </div>
              <div className="flex-1 overflow-y-auto px-4">
                {activityFeed.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-600">
                    No activity yet.
                  </div>
                ) : (
                  activityFeed.map((entry) => (
                    <ActivityFeedItem key={entry.id} entry={entry} />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="flex-shrink-0 border-t border-slate-700/30 bg-[#0d1117]/60 px-6 py-3">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between text-xs text-slate-600">
          <span>CAO Platform Org Chart POC &mdash; NeuralQuantum.ai &copy; 2026</span>
          <span className="hidden sm:inline">
            Isolated POC &mdash; not connected to production Nellie
          </span>
          <span>
            Dashboard v0.1 &mdash; React 18 + Tailwind CSS
          </span>
        </div>
      </footer>
    </div>
  );
}
